import { queryOptions, useQuery } from '@tanstack/react-query';
import { SelfServiceSessionService } from '../../generated';
import { ApiError } from '../../../components/ApiLoadingGuard/ApiLoadingGuard';
import { ResourceStatus } from '../../resources/useOcInsightsEvents';
import { MaterialClassification, transformMaterialClassification } from '../materialClassification';

const selfServiceMaterialClassificationKeys = {
  base: () => ['selfServiceMaterialClassification'] as const,
  one: ({
    jobsiteId,
    sessionId,
    imageIndex,
    materialTypeStatus,
    imageStatus,
  }: SelfServiceMaterialClassificationOptionProps) =>
    [
      ...selfServiceMaterialClassificationKeys.base(),
      jobsiteId,
      sessionId,
      imageIndex,
      materialTypeStatus,
      imageStatus,
    ] as const,
};

interface SelfServiceMaterialClassificationOptionProps {
  jobsiteId: string;
  sessionId: string;
  imageIndex: number | null;
  materialTypeStatus: ResourceStatus;
  imageStatus: ResourceStatus;
  enabled?: boolean;
}

function selfServiceMaterialClassificationOptions({
  jobsiteId,
  sessionId,
  imageIndex,
  materialTypeStatus,
  imageStatus,
  enabled = true,
}: SelfServiceMaterialClassificationOptionProps) {
  return queryOptions<MaterialClassification, ApiError>({
    queryKey: selfServiceMaterialClassificationKeys.one({
      jobsiteId,
      sessionId,
      imageIndex,
      materialTypeStatus,
      imageStatus,
    }),
    queryFn: async () => {
      if (!jobsiteId || !sessionId || imageIndex === null)
        throw new Error('jobsiteId, sessionId and imageIndex are required');

      const response =
        await SelfServiceSessionService.selfServiceSessionControllerGetSessionImageMaterialClassification({
          path: { jobsiteId, sessionId, index: imageIndex },
        });

      if (response.data !== undefined) {
        return transformMaterialClassification(response.data.output || []);
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled:
      enabled &&
      !!jobsiteId &&
      !!sessionId &&
      imageIndex !== null &&
      imageStatus === 'finished' &&
      materialTypeStatus === 'finished',
  });
}

export function useSelfServiceMaterialClassification(props: SelfServiceMaterialClassificationOptionProps) {
  return useQuery(selfServiceMaterialClassificationOptions(props));
}
